/**
 * Global session save service for handling Sign Up button clicks
 * This service allows any page to register its current state for saving
 * when the Header Sign Up button is clicked.
 */

interface SessionData {
  tickerList: string[];
  listName: string;
  hiddenStatistics: {
    daily?: string[];
    technicals?: string[];
    fundamentals?: string[];
    analystViews?: string[];
  };
}

class SessionSaveService {
  private currentSessionData: SessionData | null = null;
  private listeners: Array<(data: SessionData) => void> = [];

  /**
   * Register current session data from a page
   */
  registerSessionData(data: SessionData) {
    this.currentSessionData = data;
    console.log('📝 Session data registered:', {
      tickerList: data.tickerList,
      listName: data.listName,
      hiddenStatistics: data.hiddenStatistics,
      tickerCount: data.tickerList.length
    });
  }

  /**
   * Get current session data
   */
  getSessionData(): SessionData | null {
    return this.currentSessionData;
  }

  /**
   * Save current session data to sessionStorage
   * This is called when the Header Sign Up button is clicked
   */
  saveCurrentSessionToStorage(): boolean {
    console.log('🔍 saveCurrentSessionToStorage called, currentSessionData:', this.currentSessionData);
    
    if (!this.currentSessionData) {
      console.log('⚠️ No session data to save');
      return false;
    }

    const { tickerList, listName, hiddenStatistics } = this.currentSessionData;
    
    
    // Save to sessionStorage (same as Save button)
    sessionStorage.setItem('stocktable-ticker-list', JSON.stringify(tickerList));
    sessionStorage.setItem('stocktable-list-name', listName);
    
    // Merge hidden statistics with existing data instead of overwriting
    const existingHiddenStats = sessionStorage.getItem('stocktable-hidden-statistics');
    const existingObject = existingHiddenStats ? JSON.parse(existingHiddenStats) : {};
    const mergedObject = {
      ...existingObject,
      ...hiddenStatistics
    };
    sessionStorage.setItem('stocktable-hidden-statistics', JSON.stringify(mergedObject));
    
    
    console.log('💾 Session data saved to storage:', {
      tickerList,
      listName,
      hiddenStatistics,
      tickerCount: tickerList.length
    });

    // Notify listeners
    this.listeners.forEach(listener => listener(this.currentSessionData!));
    
    return true;
  }

  /**
   * Add a listener for when session data is saved
   */
  addSaveListener(listener: (data: SessionData) => void) {
    this.listeners.push(listener);
  }

  /**
   * Remove a listener
   */
  removeSaveListener(listener: (data: SessionData) => void) {
    this.listeners = this.listeners.filter(l => l !== listener);
  }

  /**
   * Clear current session data
   */
  clearSessionData() {
    this.currentSessionData = null;
  }
}

// Export singleton instance
export const sessionSaveService = new SessionSaveService();
